// GA4-standard ecommerce events, pushed onto the GTM dataLayer.
//
// Follows Google's recommended ecommerce schema: each event carries an
// `ecommerce` object with an `items` array, and we push `{ ecommerce: null }`
// first to clear any previous items so they never merge across events.
// In GTM: enable "Use Data Layer" ecommerce on the GA4 Event tags and add a
// Custom Event trigger per event name below.

import type { CampData } from '../types/camp'
import type { Locale } from '../i18n/LocaleContext'
import { pushDataLayer } from './analytics'

export const CURRENCY = 'RON'

export interface GA4Item {
  item_id: string
  item_name: string
  item_category?: string
  price?: number
  quantity?: number
}

/** Numeric price for a camp: prefer `priceAmount`, else parse the "1.200 RON" string. */
export function campPrice(camp: CampData): number {
  if (typeof camp.priceAmount === 'number') return camp.priceAmount
  const digits = camp.price.replace(/[^\d]/g, '')
  return digits ? parseInt(digits, 10) : 0
}

/** Map a camp (+ quantity) to a GA4 ecommerce item. */
export function campToItem(camp: CampData, locale: Locale, quantity = 1): GA4Item {
  return {
    item_id: camp.id,
    item_name: camp.title[locale],
    item_category: camp.category[locale],
    price: campPrice(camp),
    quantity,
  }
}

// Clear then push, per Google's guidance.
function pushEcommerce(event: string, ecommerce: Record<string, unknown>): void {
  pushDataLayer({ ecommerce: null })
  pushDataLayer({ event, ecommerce })
}

/** Camp list shown (e.g. registration step 1). */
export function viewItemList(
  camps: CampData[],
  locale: Locale,
  listId: string,
  listName: string,
): void {
  pushEcommerce('view_item_list', {
    item_list_id: listId,
    item_list_name: listName,
    items: camps.map((c) => ({
      ...campToItem(c, locale),
      item_list_id: listId,
      item_list_name: listName,
    })),
  })
}

/** A camp was chosen from a list. */
export function selectItem(
  camp: CampData,
  locale: Locale,
  listId: string,
  listName: string,
): void {
  pushEcommerce('select_item', {
    item_list_id: listId,
    item_list_name: listName,
    items: [{ ...campToItem(camp, locale), item_list_id: listId, item_list_name: listName }],
  })
}

/** A camp's details were viewed (detail modal). */
export function viewItem(camp: CampData, locale: Locale): void {
  pushEcommerce('view_item', {
    currency: CURRENCY,
    value: campPrice(camp),
    items: [campToItem(camp, locale)],
  })
}

/** The registration/checkout flow was entered for a camp. */
export function beginCheckout(camp: CampData, locale: Locale, quantity: number): void {
  const item = campToItem(camp, locale, quantity)
  pushEcommerce('begin_checkout', {
    currency: CURRENCY,
    value: (item.price ?? 0) * quantity,
    items: [item],
  })
}

/** Payment info submitted / heading to the payment provider. */
export function addPaymentInfo(
  camp: CampData,
  locale: Locale,
  quantity: number,
  paymentType = 'card',
): void {
  const item = campToItem(camp, locale, quantity)
  pushEcommerce('add_payment_info', {
    currency: CURRENCY,
    value: (item.price ?? 0) * quantity,
    payment_type: paymentType,
    items: [item],
  })
}

/** Payment confirmed. */
export function purchase(opts: {
  transactionId: string | number
  value: number
  currency: string
  item: GA4Item
}): void {
  pushEcommerce('purchase', {
    transaction_id: String(opts.transactionId),
    value: opts.value,
    currency: opts.currency,
    items: [opts.item],
  })
}
